/**
 * Learn F1 — content index.
 *
 * All topic content is hand-written in the category modules (no templates).
 * This module assembles them, provides lookup helpers, and maps legacy slugs
 * from the old auto-generated topic list onto their merged replacements.
 */
import { GETTING_STARTED } from "./gettingStarted";
import { RULES_RACE_CONTROL } from "./rulesRaceControl";
import { STRATEGY } from "./strategy";
import { RACECRAFT } from "./racecraft";
import { CAR_TECH } from "./carTech";
import { CHAMPIONSHIP } from "./championship";
import { HISTORY_CULTURE } from "./historyCulture";

export const LEARN_CATEGORIES = [
    { id: "getting-started", title: "Getting Started", blurb: "The weekend, the sessions, and how to watch.", topics: GETTING_STARTED },
    { id: "rules-race-control", title: "Rules & Race Control", blurb: "Flags, safety cars, stewards, and the rulebook.", topics: RULES_RACE_CONTROL },
    { id: "strategy", title: "Race Strategy & Tires", blurb: "Compounds, degradation, pit stops, and the strategy chess.", topics: STRATEGY },
    { id: "racecraft", title: "Racecraft & Overtaking", blurb: "Slipstreams, racing lines, attack and defense.", topics: RACECRAFT },
    { id: "car-tech", title: "Car & Technology", blurb: "Aero, power units, brakes, and what the driver controls.", topics: CAR_TECH },
    { id: "championship", title: "The Championship", blurb: "Points, titles, and the money behind the racing.", topics: CHAMPIONSHIP },
    { id: "history-culture", title: "History & Culture", blurb: "Eras, legends, circuits, and the language of F1.", topics: HISTORY_CULTURE },
];

export const LEARN_TOPICS = LEARN_CATEGORIES.flatMap((category) =>
    category.topics.map((topic) => ({ ...topic, category: category.title, categoryId: category.id }))
);

const TOPIC_BY_ID = new Map(LEARN_TOPICS.map((topic) => [topic.id, topic]));

/** Old auto-generated slugs -> new topic ids (merged/renamed pages). */
export const LEGACY_REDIRECTS = {
    "formula-1": "what-is-formula-1",
    "grand-prix": "grand-prix-weekend",
    "race-weekend": "grand-prix-weekend",
    "practice-session": "practice-sessions",
    "pole-position": "qualifying",
    "sprint-race": "sprint-weekends",
    "formation-lap": "formation-lap-and-start",
    "checkered-flag": "chequered-flag",
    "championship-points": "points-system",
    "constructors-championship": "constructors-championship",
    "drivers-championship": "drivers-championship",
    "parc-ferme": "parc-ferme",
    fia: "race-control-and-fia",
    drs: "drs-rules",
    "drs-detection-zone": "drs-rules",
    "drs-activation-zone": "drs-rules",
    slipstream: "slipstream-and-tow",
    tow: "slipstream-and-tow",
    "dirty-air": "clean-dirty-air",
    "clean-air": "clean-dirty-air",
    overtake: "overtaking-moves",
    "defensive-driving": "defending-rules",
    "track-position": "pit-windows",
    "racing-line": "racing-line",
    "late-braking": "overtaking-moves",
    switchback: "overtaking-moves",
    divebomb: "overtaking-moves",
    "wheel-to-wheel-racing": "defending-rules",
    "tire-compounds": "tire-compounds",
    "soft-tire": "tire-compounds",
    "medium-tire": "tire-compounds",
    "hard-tire": "tire-compounds",
    "intermediate-tire": "wet-weather-strategy",
    "full-wet-tire": "wet-weather-strategy",
    "tire-degradation": "tire-degradation",
    "tire-wear": "tire-degradation",
    "tire-life": "tire-degradation",
    "tire-cliff": "tire-degradation",
    stint: "pit-windows",
    "tire-warm-up": "tire-management",
    "tire-temperature": "tire-management",
    "tire-pressure": "tire-management",
    "tire-management": "tire-management",
    graining: "graining-blistering",
    blistering: "graining-blistering",
    "flat-spot": "graining-blistering",
    "pit-stop": "pit-stops",
    "pit-lane": "pit-lane-rules",
    "pit-window": "pit-windows",
    undercut: "undercut",
    overcut: "overcut",
    inlap: "pit-windows",
    outlap: "undercut",
    "double-stack": "safety-car-strategy",
    "one-stop-strategy": "one-stop-two-stop",
    "two-stop-strategy": "one-stop-two-stop",
    "safety-car": "safety-car",
    "virtual-safety-car": "virtual-safety-car",
    "red-flag": "red-flags",
    "yellow-flag": "flags-explained",
    "blue-flag": "flags-explained",
    "track-limits": "track-limits",
    penalty: "penalties-and-stewarding",
    "steward-investigation": "penalties-and-stewarding",
    aerodynamics: "aerodynamics-downforce",
    downforce: "aerodynamics-downforce",
    drag: "aerodynamics-downforce",
    "ground-effect": "ground-effect-floor",
    "venturi-tunnels": "ground-effect-floor",
    floor: "ground-effect-floor",
    diffuser: "ground-effect-floor",
    "front-wing": "wings",
    "rear-wing": "wings",
    suspension: "suspension-ride",
    "power-unit": "power-unit",
    ers: "ers-energy",
    "energy-harvesting": "ers-energy",
    "energy-deployment": "ers-energy",
    "brake-bias": "brakes-brake-bias",
    brakes: "brakes-brake-bias",
    differential: "steering-wheel",
    oversteer: "balance-oversteer-understeer",
    understeer: "balance-oversteer-understeer",
    "fuel-load": "fuel-load",
    "lift-and-coast": "fuel-load",
    "track-evolution": "track-evolution",
};

export function findTopic(slug) {
    if (!slug) return null;
    const direct = TOPIC_BY_ID.get(slug);
    if (direct) return direct;
    const redirect = LEGACY_REDIRECTS[slug];
    return redirect ? TOPIC_BY_ID.get(redirect) || null : null;
}

export function relatedTopics(topic) {
    return (topic?.related || [])
        .map((id) => TOPIC_BY_ID.get(id))
        .filter(Boolean);
}

export function searchTopics(query) {
    const q = (query || "").trim().toLowerCase();
    if (!q) return LEARN_TOPICS;
    return LEARN_TOPICS.filter((topic) =>
        [topic.title, topic.summary, topic.keyTakeaway, topic.category]
            .join(" ")
            .toLowerCase()
            .includes(q)
    );
}
